import React from 'react';
import type { Theme } from '@mui/material/styles';
import clsx from 'clsx';

import makeStyles from '@mui/styles/makeStyles';
import ButtonBase, { ButtonBaseProps } from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';

import useIconParrot from 'hooks/useIconParrot';

const useStyles = makeStyles((theme: Theme) => ({
  label: {
    width: '100%', // Ensure the correct width for iOS Safari
    display: 'inherit',
    alignItems: 'inherit',
    justifyContent: 'inherit',
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.common.black,

    '&:hover': {
      color: theme.palette.text.secondary,
    },
  },

  startIcon: {
    display: 'inherit',
    marginRight: 16,
    marginLeft: -4,

    '&$iconSizeSmall': {
      marginLeft: -2,
    },
  },

  disabledLabel: {
    color: theme.palette.darkGrey[15],
  },

  redLabel: {
    color: theme.palette.error.main,
  },
}));

interface Props extends ButtonBaseProps {
  children?: React.ReactNode;
  name?: string;
  loading?: boolean;
  loadingText?: string;
  disabled?: boolean;
  isRed?: boolean;
}

function ButtonExportExcel({
  children,
  name,
  loading = false,
  loadingText = '',
  disabled = false,
  isRed = false,
  ...others
}: Props) {
  const classes = useStyles();

  const { IconExcel, IconExcelDisabled, IconExcelRed } = useIconParrot({
    icons: ['excel', 'excelDisabled', 'excelRed'],
  });

  const isRedIcon = isRed ? <IconExcelRed /> : <IconExcel />;
  const renderIcon = disabled ? <IconExcelDisabled /> : isRedIcon;

  return (
    <ButtonBase data-cy={name} disableRipple disableTouchRipple disabled={disabled} {...others}>
      <span
        className={clsx(classes.label, {
          [classes.disabledLabel]: disabled,
          [classes.redLabel]: isRed,
        })}
      >
        <span className={classes.startIcon}>
          {loading ? <CircularProgress color="inherit" size={18} /> : renderIcon}
        </span>

        {loading ? loadingText : children}
      </span>
    </ButtonBase>
  );
}

export default ButtonExportExcel;
